import { FileQuestion, Plus, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation } from '#router'
import { Button } from '@/components/ui/button'
import { useCoachingQuestions } from './hooks/use-questions'
import { MasterQuestionsTable } from './master-questions-table'
import type { CoachingQuestion } from './types'

export function ProblemsList() {
  const {
    questions,
    sources,
    loading,
    loadingMore,
    totalCount,
    error,
    refresh,
    deleteQuestion,
    updateQuestion,
  } = useCoachingQuestions()

  const { navigate } = useLocation()

  // Custom sources management (for sources added but not yet in any question)
  const [customSources, _setCustomSources] = useState<string[]>([])
  const allSources = [...new Set([...sources, ...customSources])].sort()

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      await deleteQuestion(questionId)
      toast.success('Problem deleted successfully')
    } catch {
      toast.error('Failed to delete problem')
    }
  }

  const handleSelectQuestion = (question: CoachingQuestion) => {
    navigate(`/admin/problems/${question.id}`)
  }

  const handleAddProblem = () => {
    navigate('/admin/problems/new')
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3">
          <RefreshCw size={20} className="animate-spin" />
          <span>Loading problems...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto w-full px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 p-2">
                <FileQuestion size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-foreground text-xl font-bold">
                  Problem Bank
                </h1>
                <p className="text-muted-foreground flex items-center gap-2 text-sm">
                  {totalCount !== null ? totalCount : questions.length} total
                  problems
                  {loadingMore && (
                    <span className="flex items-center gap-1 text-xs">
                      <RefreshCw size={12} className="animate-spin" />
                      Loading more...
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={handleAddProblem}>
                <Plus size={18} className="mr-2" />
                Add Problem
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto w-full px-4 py-6">
        {error ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <FileQuestion size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              Unable to load problems
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={refresh} variant="outline">
              <RefreshCw size={18} className="mr-2" />
              Retry
            </Button>
          </div>
        ) : questions.length === 0 ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <FileQuestion size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              No problems yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Add your first problem to get started building your question bank.
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={handleAddProblem}>
                <Plus size={18} className="mr-2" />
                Add Problem
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-card border-border overflow-hidden rounded-lg border p-4">
            <MasterQuestionsTable
              questions={questions}
              sources={allSources}
              onDeleteQuestion={handleDeleteQuestion}
              onSelectQuestion={handleSelectQuestion}
              onUpdateQuestion={updateQuestion}
              persistState
            />
          </div>
        )}
      </main>
    </div>
  )
}
